import { STATE_NUMBERS } from "./formIdUtils";

const PLACEHOLDER_FORM_ID = "00000000-0000-0000-0000-000000000000";

const SEGMENT_TO_LOCALE = Object.fromEntries(
  Object.entries(STATE_NUMBERS).map(([locale, seg]) => [seg, locale]),
);

export function getExpectedStateSegment(locale) {
  return STATE_NUMBERS[locale] || null;
}

export function getStateSegmentFromFormId(formId) {
  if (!formId || typeof formId !== "string") return null;
  const m = formId.match(/^00000000-([0-9]{4})-/);
  return m ? m[1] : null;
}

export function inferLocaleFromFormId(formId) {
  const seg = getStateSegmentFromFormId(formId);
  return seg ? SEGMENT_TO_LOCALE[seg] || null : null;
}

export function getLocaleSlice(registry, locale) {
  if (!registry?.locales || !locale) return null;
  return registry.locales[locale] || null;
}

// Registry values are plain type strings: locales[locale][formId] = "type".
function normalizeType(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export function getRegistryRows(registry, locale) {
  const slice = getLocaleSlice(registry, locale);
  if (!slice) return [];
  return Object.entries(slice).map(([formId, type]) => ({
    formId,
    type: normalizeType(type),
  }));
}

export function lookupByFormId(registry, locale, formId) {
  const slice = getLocaleSlice(registry, locale);
  if (!slice || !formId) return null;
  if (!Object.prototype.hasOwnProperty.call(slice, formId)) return null;
  return { formId, type: normalizeType(slice[formId]) };
}

export function lookupByType(registry, locale, type) {
  if (!type) return [];
  return getRegistryRows(registry, locale).filter((r) => r.type === type);
}

/**
 * Every distinct form type in the registry, across ALL locales, sorted.
 *
 * One type means one thing everywhere — the same semantic carries the same name in every
 * state — so the vocabulary is registry-wide, not per locale. New types are added through
 * the controlled bundle → build-form-registry.js process, never from Studio.
 */
export function getAllRegistryTypes(registry) {
  const types = new Set();
  for (const slice of Object.values(registry?.locales ?? {})) {
    for (const value of Object.values(slice ?? {})) {
      const type = normalizeType(value);
      if (type) types.add(type);
    }
  }
  return [...types].sort((a, b) => a.localeCompare(b));
}

/**
 * Match a typed form type against the registry vocabulary, ignoring case.
 *
 * `exact` is the registry's spelling of the same word — use it to correct the case of what
 * the author typed ("counteroffer" → "counterOffer"). When there is no exact match,
 * `suggestions` holds the registry types that START with what was typed, so a half-typed
 * name can be finished by hand. Both empty means the type is not in the registry.
 *
 * @param {object|null} registry
 * @param {string} value the typed form type
 * @param {{ limit?: number }} [options] max suggestions (default 5)
 * @returns {{ exact: string|null, suggestions: string[] }}
 */
export function matchRegistryType(registry, value, options = {}) {
  const typed = String(value ?? "").trim();
  if (!typed) return { exact: null, suggestions: [] };

  const limit = options.limit ?? 5;
  const lower = typed.toLowerCase();
  const known = getAllRegistryTypes(registry);

  const exact = known.find((t) => t.toLowerCase() === lower) || null;
  if (exact) return { exact, suggestions: [] };

  return {
    exact: null,
    suggestions: known.filter((t) => t.toLowerCase().startsWith(lower)).slice(0, limit),
  };
}

function buildSuggestedFix(registryRow) {
  if (!registryRow) return null;
  return {
    formId: registryRow.formId,
    type: registryRow.type,
  };
}

/**
 * @param {{ locale?: string, formId?: string, type?: string }} formDetails
 * @param {object|null} registry
 * @param {{ strict?: boolean }} [options]
 */
export function validateFormIdentity(formDetails, registry, options = {}) {
  const strict = options.strict === true;
  const errors = [];
  const warnings = [];

  if (!registry?.locales) {
    return {
      valid: true,
      errors,
      warnings,
      registryRow: null,
      suggestedFix: null,
      skipped: true,
    };
  }

  const locale = (formDetails?.locale || "").trim();
  const formId = (formDetails?.formId || "").trim();
  const type = (formDetails?.type || "").trim();

  if (!locale) {
    return {
      valid: true,
      errors,
      warnings,
      registryRow: null,
      suggestedFix: null,
      skipped: true,
    };
  }

  const slice = getLocaleSlice(registry, locale);
  if (!slice) {
    return {
      valid: true,
      errors,
      warnings,
      registryRow: null,
      suggestedFix: null,
      skipped: true,
    };
  }

  if (!formId || formId === PLACEHOLDER_FORM_ID) {
    errors.push({ code: "PLACEHOLDER_FORM_ID", formId: formId || null });
  }

  const expectedSeg = getExpectedStateSegment(locale);
  const actualSeg = getStateSegmentFromFormId(formId);
  if (formId && expectedSeg && actualSeg && actualSeg !== expectedSeg) {
    errors.push({
      code: "LOCALE_PREFIX_MISMATCH",
      locale,
      formId,
      expectedSegment: expectedSeg,
      actualSegment: actualSeg,
    });
  }

  const inferredLocale = inferLocaleFromFormId(formId);
  if (
    formId &&
    inferredLocale &&
    locale &&
    inferredLocale !== locale
  ) {
    errors.push({
      code: "LOCALE_FORM_ID_CROSS",
      locale,
      formId,
      inferredLocale,
    });
  }

  const registryRow = lookupByFormId(registry, locale, formId);

  if (formId && formId !== PLACEHOLDER_FORM_ID && !registryRow) {
    errors.push({ code: "REGISTRY_MISS", locale, formId });
  }

  if (registryRow) {
    if (registryRow.type === null || registryRow.type === "") {
      errors.push({
        code: "UNREGISTERED_TYPE_SLOT",
        locale,
        formId,
      });
    } else if (type && registryRow.type !== type) {
      errors.push({
        code: "TYPE_MISMATCH",
        locale,
        formId,
        expectedType: registryRow.type,
        actualType: type,
      });
    }
  }

  if (type) {
    const otherSlots = lookupByType(registry, locale, type).filter(
      (r) => r.formId !== formId,
    );
    if (otherSlots.length > 0) {
      warnings.push({
        code: "TYPE_ON_OTHER_SUFFIX",
        locale,
        type,
        otherFormIds: otherSlots.map((r) => r.formId),
      });
    }
  }

  const valid = errors.length === 0;
  return {
    valid,
    errors,
    warnings: strict ? warnings : warnings,
    registryRow,
    suggestedFix: buildSuggestedFix(registryRow),
    skipped: false,
  };
}

export function formatRegistryValidationMessage(result, formDetails) {
  if (!result || result.skipped) return null;
  if (result.valid) {
    const row = result.registryRow;
    if (!row) return null;
    const loc = formDetails?.locale || "";
    return `Registry (${loc}): ${row.type}`;
  }
  const err = result.errors[0];
  if (!err) return "Registry validation failed.";
  switch (err.code) {
    case "TYPE_MISMATCH":
      return `Registry (${err.locale}): type '${err.actualType}' does not match ${err.formId?.slice(-4)} (expected '${err.expectedType}')`;
    case "REGISTRY_MISS":
      return `Registry (${err.locale}): Form ID ${err.formId} is not in the registry.`;
    case "PLACEHOLDER_FORM_ID":
      return "Registry: Form ID is missing or placeholder.";
    case "LOCALE_PREFIX_MISMATCH":
      return `Registry (${err.locale}): Form ID segment ${err.actualSegment} does not match locale (expected ${err.expectedSegment}).`;
    case "LOCALE_FORM_ID_CROSS":
      return `Registry: Form ID belongs to locale ${err.inferredLocale}, not ${err.locale}.`;
    case "UNREGISTERED_TYPE_SLOT":
      return `Registry (${err.locale}): Form ID ${err.formId?.slice(-4)} is a placeholder slot (no registered type).`;
    default:
      return `Registry validation failed: ${err.code}`;
  }
}
